import {
  AmenityIcon,
  CommunityIcon,
  DashboardIcon,
  DeliverIcon,
  FrontDeskIcon,
  DevicesIcon,
  EventSearchIcon,
  IntegrationIcon,
  LicenseIcon,
  PropertyIcon,
  SettingsIcon,
  TenantIcon,
} from 'src/components/Icons/menu';
/** Workflow grouping for the sidebar (Dashboard is standalone and has no section) */
export type NavSectionId = 'setup' | 'operations' | 'system';

export interface Menu {
  title: string;
  icon?: JSX.Element;
  path: string;
  children?: Menu[];
  allowedRoles?: string[];
  section?: NavSectionId;
}

export const allMenus: Menu[] = [
  {
    title: 'Dashboard',
    icon: <DashboardIcon />,
    path: '/dashboard',
    allowedRoles: ['Org Operator', 'Org Admin', 'Org Owner', 'Org Manager'],
  },
  {
    title: 'Properties',
    icon: <PropertyIcon />,
    path: '/property',
    section: 'setup',
    allowedRoles: ['Org Admin', 'Org Owner', 'Org Manager'],
  },
  {
    title: 'Residents',
    icon: <TenantIcon />,
    path: '/tenant',
    section: 'setup',
    allowedRoles: ['Org Admin', 'Org Owner', 'Org Manager'],
  },
  {
    title: 'Devices & Access',
    icon: <DevicesIcon />,
    path: '/devices',
    section: 'setup',
    allowedRoles: ['Org Admin', 'Org Owner', 'Org Manager'],
  },
  {
    title: 'Front Desk',
    icon: <FrontDeskIcon />,
    path: '/front-desk',
    section: 'operations',
    allowedRoles: ['Org Operator', 'Org Admin', 'Org Owner', 'Org Manager'],
  },
  {
    title: 'Deliveries',
    icon: <DeliverIcon />,
    path: '/delivery',
    section: 'operations',
    allowedRoles: ['Org Operator', 'Org Admin', 'Org Owner', 'Org Manager'],
  },
  {
    title: 'Amenities',
    icon: <AmenityIcon />,
    path: '/amenities',
    section: 'operations',
    allowedRoles: ['Org Operator', 'Org Admin', 'Org Owner', 'Org Manager'],
  },
  {
    title: 'Activity',
    icon: <EventSearchIcon />,
    path: '/eventSearch',
    section: 'operations',
    allowedRoles: ['Org Operator', 'Org Admin', 'Org Owner', 'Org Manager'],
  },
  {
    title: 'Settings',
    icon: <SettingsIcon />,
    path: 'settings',
    section: 'system',
    allowedRoles: ['Org Admin', 'Org Owner'],
    children: [
      {
        title: 'Users',
        icon: <CommunityIcon />,
        path: '/settings/users',
        allowedRoles: ['Org Admin', 'Org Owner'],
      },
      {
        title: 'Integration',
        icon: <IntegrationIcon />,
        path: '/settings/integration',
        allowedRoles: ['Org Admin', 'Org Owner'],
      },
      {
        title: 'License',
        icon: <LicenseIcon />,
        path: '/settings/license',
        allowedRoles: ['Org Admin', 'Org Owner'],
      },
    ],
  },
];

export const getFilteredMenus = (userRole?: string): Menu[] => {
  if (!userRole) return [];

  return allMenus
    .filter(menu => {
      if (!menu.allowedRoles) return true;
      return menu.allowedRoles.includes(userRole);
    })
    .map(menu => {
      if (!menu.children?.length) return menu;
      const filteredChildren = menu.children.filter(
        child => !child.allowedRoles || child.allowedRoles.includes(userRole)
      );
      return {...menu, children: filteredChildren};
    });
};

export const getLicenseFilteredMenus = (userRole?: string, isAtLimit?: boolean): Menu[] => {
  if (!userRole) return [];

  const roleFilteredMenus = getFilteredMenus(userRole);

  if (!isAtLimit) return roleFilteredMenus;

  return roleFilteredMenus
    .map(menu => {
      if (menu.path === '/dashboard') {
        return menu;
      }

      if (menu.path === 'settings' && menu.children) {
        const filteredChildren = menu.children.filter(child => child.path === '/settings/license');
        if (filteredChildren.length > 0) {
          return {...menu, children: filteredChildren};
        }
      }

      return null;
    })
    .filter((menu): menu is Menu => menu !== null);
};

export const menus = allMenus;
